import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { API, useApp } from "../context";
import { getActiveLearnerId } from "../hooks/useActiveLearner";
import { isPromptFree } from "../lib/promptFree";

// Friendly "your free premium ended — upgrade to keep premium" modal, shown
// once to a logged-in non-premium user after the site-wide birthday launch
// window has ended (within the last 14 days). Purely a gentle upsell — fully
// dismissible and never blocks the app.
export default function PostLaunchOfferModal() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  const check = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`${API}/premium-preview/active`);
      if (data?.active || !data?.recently_ended || !data?.ended_at) return;
      const key = `iw:postlaunch-dismissed:${data.ended_at}`;
      if (localStorage.getItem(key)) return;
      // Only for users who are NOT premium now.
      const learnerId = getActiveLearnerId(user.id);
      const r = await axios.get(`${API}/entitlements/resolve/${learnerId || user.id}`);
      if (r.data?.is_premium) return;
      setShow(data.ended_at);
    } catch (_) { /* ignore */ }
  }, [user?.id]);

  useEffect(() => {
    check();
  }, [check]);

  if (isPromptFree()) return null; // free-premium window: no upgrade prompts
  if (!user?.id || !show) return null;

  const dismiss = () => {
    try { localStorage.setItem(`iw:postlaunch-dismissed:${show}`, "1"); } catch (_) {}
    setShow(false);
  };
  const upgrade = () => {
    try { localStorage.setItem(`iw:postlaunch-dismissed:${show}`, "1"); } catch (_) {}
    setShow(false);
    navigate("/subscribe");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="post-launch-offer-modal">
      <div className="absolute inset-0 bg-black/70" onClick={dismiss} />
      <div
        className="relative w-full max-w-sm rounded-3xl border-2 border-sky-400/40 p-6 text-center"
        style={{ background: "linear-gradient(180deg, rgba(8,20,40,0.98), rgba(20,24,60,0.98))", boxShadow: "0 0 80px rgba(56,189,248,0.3)" }}
      >
        <button onClick={dismiss} data-testid="post-launch-offer-close" className="absolute top-3 right-3 text-white/40 hover:text-white">
          <X size={18} />
        </button>
        <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-sky-500/20 flex items-center justify-center text-3xl">🎉</div>
        <h3 className="text-white font-black text-xl mb-1" data-testid="post-launch-offer-headline">
          Your free premium has ended
        </h3>
        <p className="text-white/70 text-sm mb-5" data-testid="post-launch-offer-body">
          We hope you loved celebrating Isaiah&apos;s 9th birthday! The free premium
          period is over — upgrade to keep all the premium tools and features without missing a beat.
        </p>
        <button
          onClick={upgrade}
          data-testid="post-launch-offer-cta"
          className="w-full py-3 rounded-xl text-white font-black text-sm mb-2"
          style={{ background: "linear-gradient(135deg,#0ea5e9,#6366f1)" }}
        >
          Upgrade to keep premium
        </button>
        <button onClick={dismiss} data-testid="post-launch-offer-later" className="text-white/40 hover:text-white/70 text-xs font-semibold">
          Maybe later
        </button>
      </div>
    </div>
  );
}
